import { Outlet, Link, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { useTheme } from '../../contexts/ThemeContext';
import Icon from '../common/Icon';
import ChatPanel, { ChatLauncher } from '../ai/ChatPanel';
import NotificationBell from './NotificationBell';
import CommandPalette from '../common/CommandPalette';
import { matterApi, matterDisplayName } from '../../api';
import Select from '../common/Select';
import { humanise } from '../../lib/labels';

type IconName = Parameters<typeof Icon>[0]['name'];
type NavItem = { to: string; label: string; icon: IconName };

const navGroups: { label: string; items: NavItem[] }[] = [
  {
    label: 'Operations',
    items: [
      { to: '/',            label: 'Dashboard', icon: 'grid' },
      { to: '/matters',     label: 'Matters',   icon: 'briefcase' },
      { to: '/tasks',       label: 'Tasks',     icon: 'check-list' },
      { to: '/diary',       label: 'Diary',     icon: 'calendar' },
    ],
  },
  {
    label: 'People',
    items: [
      { to: '/parties',     label: 'Parties',   icon: 'users' },
      { to: '/attorneys',   label: 'Attorneys', icon: 'balance-scale' },
    ],
  },
  {
    label: 'Billing',
    items: [
      { to: '/invoices',    label: 'Invoices',  icon: 'receipt' },
      { to: '/fee-book',    label: 'Fee Book',  icon: 'book-open' },
      { to: '/trust',       label: 'Trust',     icon: 'shield-check' },
    ],
  },
  {
    label: 'Documents',
    items: [
      { to: '/documents',  label: 'Library',    icon: 'document' },
      { to: '/precedents', label: 'Precedents', icon: 'book-open' },
      { to: '/signing',    label: 'Signing',    icon: 'pen' },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/settings', label: 'Settings', icon: 'cog' },
    ],
  },
];

const platformNav: NavItem[] = [
  { to: '/platform-admin',       label: 'Tenants', icon: 'globe' },
  { to: '/platform-admin/plans', label: 'Plans',   icon: 'credit-card' },
];

export default function AppShell() {
  const { user, memberships, logout } = useAuth();
  const { active, selectTenant } = useTenant();
  const { theme, toggle: toggleTheme } = useTheme();
  const loc = useLocation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [matterCtx, setMatterCtx] = useState<{ id: number; reference: string; name: string } | null>(null);

  // Global ⌘K / Ctrl+K toggles the command palette.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const matterIdInPath = useMemo(() => {
    const m = loc.pathname.match(/^\/matters\/(\d+)/);
    return m ? Number(m[1]) : null;
  }, [loc.pathname]);

  useEffect(() => {
    let cancelled = false;
    if (!matterIdInPath) { setMatterCtx(null); return; }
    matterApi.get(matterIdInPath)
      .then((r) => {
        if (cancelled) return;
        setMatterCtx({ id: r.matter.ROWID, reference: r.matter.reference, name: matterDisplayName(r.matter) });
      })
      .catch(() => { if (!cancelled) setMatterCtx(null); });
    return () => { cancelled = true; };
  }, [matterIdInPath]);

  // Breadcrumb derived from the current route. Each crumb is `{ to, label }`;
  // the last crumb is rendered as the current-page emphasis (no link).
  const crumbs = useMemo(() => buildCrumbs(loc.pathname, matterCtx), [loc.pathname, matterCtx]);

  const isActive = (to: string) => {
    if (to === '/') return loc.pathname === '/';
    return loc.pathname === to || loc.pathname.startsWith(to + '/');
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--color-bg)' }}>
      {/* --- SIDEBAR --- */}
      <aside style={sidebarStyle}>
        <div style={{ padding: '18px 20px 14px', display: 'flex', alignItems: 'center', minHeight: 64 }}>
          <img src="/app/litigate-logo.png" alt="Litigate" style={{ height: 40, maxWidth: '100%', display: 'block' }} />
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '6px 10px 16px' }}>
          {navGroups.map((g) => (
            <div key={g.label} style={{ marginBottom: 14 }}>
              <div style={sidebarSectionLabel}>{g.label}</div>
              {g.items.map((item) => (
                <SidebarLink key={item.to} to={item.to} label={item.label} icon={item.icon} active={isActive(item.to)} />
              ))}
            </div>
          ))}

          {user?.is_platform_admin && (
            <div>
              <div style={{ ...sidebarSectionLabel, color: 'rgba(229, 85, 60, 0.7)' }}>Platform</div>
              {platformNav.map((item) => (
                <SidebarLink key={item.to} to={item.to} label={item.label} icon={item.icon} active={isActive(item.to)} />
              ))}
            </div>
          )}
        </div>

        {/* User profile at bottom */}
        <div style={{ padding: '14px 12px', borderTop: '1px solid rgba(255,255,255,0.08)', position: 'relative' }}>
          <button
            onClick={() => setUserMenuOpen((v) => !v)}
            style={{
              width: '100%',
              display: 'flex', alignItems: 'center', gap: 10,
              background: 'transparent', border: 'none',
              padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
              color: '#E8ECF2',
            }}
          >
            <Avatar name={user?.display_name || user?.email || 'U'} />
            <div style={{ textAlign: 'left', minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.display_name || user?.email}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.email}
              </div>
            </div>
            <Icon name="chevron-down" size={14} style={{ color: 'rgba(255,255,255,0.55)' }} />
          </button>
          {userMenuOpen && (
            <div style={userMenuStyle}>
              <Link to="/settings/profile" onClick={() => setUserMenuOpen(false)} style={menuItemStyle}>
                <Icon name="user" size={14} /> My profile
              </Link>
              <button onClick={() => { setUserMenuOpen(false); logout(); }} style={{ ...menuItemStyle, border: 'none', background: 'transparent', width: '100%', textAlign: 'left' }}>
                <Icon name="logout" size={14} /> Sign out
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* --- MAIN --- */}
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <header style={topbarStyle}>
          {/* LEFT — breadcrumb trail. Pages get a single crumb; deep routes
              (matters/:id/sub) get the full chain. The last crumb is bold
              and not linked because the user is already there. */}
          <nav style={breadcrumbStyle} aria-label="Breadcrumb">
            {crumbs.map((c, i) => {
              const isLast = i === crumbs.length - 1;
              return (
                <span key={`${c.to ?? c.label}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  {i > 0 && <span style={breadcrumbDividerStyle}>/</span>}
                  {isLast || !c.to ? (
                    <span style={breadcrumbCurrentStyle}>{c.label}</span>
                  ) : (
                    <Link to={c.to} style={breadcrumbSegStyle}>{c.label}</Link>
                  )}
                </span>
              );
            })}
          </nav>

          {/* Spacer pushes search to the centre. */}
          <div style={{ flex: 1 }} />

          {/* CENTRE — command-palette trigger. */}
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            style={searchTriggerStyle}
            aria-label="Open command palette"
          >
            <Icon name="search" size={16} style={{ color: 'var(--color-text-muted)' }} />
            <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--color-text-muted)' }}>
              Search or jump to…
            </span>
            <kbd style={triggerKbdStyle}>⌘K</kbd>
          </button>

          <div style={{ flex: 1 }} />

          {/* RIGHT cluster — utilities first, divider, then chambers identity. */}
          <div className="row" style={{ gap: 10 }}>
            <IconButton onClick={toggleTheme} title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}>
              <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={18} />
            </IconButton>
            <NotificationBell />

            <div style={dividerStyle} />

            {active && (
              <div style={tenantBadge}>
                <Icon name="pin" size={14} style={{ color: 'var(--color-accent)' }} />
                <div style={{ lineHeight: 1.1 }}>
                  <div style={{ fontSize: 10, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Chambers</div>
                  {memberships.length > 1 ? (
                    <Select
                      compact
                      value={String(active.tenant_id)}
                      onChange={(v) => selectTenant(v)}
                      searchPlaceholder="Switch chambers…"
                      style={{ width: 200 }}
                      options={memberships.map((m) => ({ value: String(m.tenant_id), label: m.tenant_name }))}
                    />
                  ) : (
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{active.tenant_name}</div>
                  )}
                </div>
              </div>
            )}

            {/* Avatar chip — links to profile. The full sign-out menu still
                lives in the sidebar to avoid duplicating the dropdown UX. */}
            <Link to="/settings/profile" style={avatarChipStyle} title={user?.display_name || user?.email || 'Profile'}>
              <Avatar name={user?.display_name || user?.email || 'U'} />
            </Link>
          </div>
        </header>

        <section style={{ padding: 28, flex: 1, overflow: 'auto' }}>
          <Outlet />
        </section>
      </main>

      <ChatLauncher onClick={() => setChatOpen(true)} active={chatOpen} />
      <ChatPanel open={chatOpen} onClose={() => setChatOpen(false)} matterContext={matterCtx} />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}

// ---------- styles ----------

const sidebarStyle: React.CSSProperties = {
  width: 240,
  background: 'var(--color-sidebar)',
  color: '#E8ECF2',
  borderRight: '1px solid rgba(255,255,255,0.06)',
  display: 'flex', flexDirection: 'column',
  position: 'sticky', top: 0, height: '100vh',
  flexShrink: 0,
};

const sidebarSectionLabel: React.CSSProperties = {
  fontSize: 10,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.38)',
  fontWeight: 600,
  padding: '10px 12px 8px',
};

const topbarStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '10px 20px',
  height: 64,
  background: 'var(--color-topbar)',
  borderBottom: '1px solid var(--color-border)',
  position: 'sticky', top: 0, zIndex: 10,
};

const breadcrumbStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 8,
  fontSize: 14,
  flexShrink: 0,
  minWidth: 0,
  maxWidth: 360,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
};

const breadcrumbSegStyle: React.CSSProperties = {
  color: 'var(--color-text-muted)',
  textDecoration: 'none',
};

const breadcrumbDividerStyle: React.CSSProperties = {
  color: 'var(--color-border-strong)',
};

const breadcrumbCurrentStyle: React.CSSProperties = {
  color: 'var(--color-text)',
  fontWeight: 600,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: 280,
};

const avatarChipStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center',
  width: 36, height: 36,
  borderRadius: '50%',
  border: '1px solid var(--color-border)',
  background: 'var(--color-surface-2)',
  textDecoration: 'none',
  flexShrink: 0,
  overflow: 'hidden',
};

const searchTriggerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10,
  height: 36, padding: '0 12px',
  borderRadius: 10,
  border: '1px solid var(--color-border)',
  background: 'var(--color-surface-2)',
  color: 'var(--color-text-muted)',
  cursor: 'text',
  fontSize: 13,
  minWidth: 280, maxWidth: 460,
  flexShrink: 0,
};

const triggerKbdStyle: React.CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 11,
  padding: '2px 6px',
  borderRadius: 6,
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text-subtle)',
};

const tenantBadge: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '6px 12px',
  background: 'var(--color-surface-2)',
  border: '1px solid var(--color-border)',
  borderRadius: 10,
};

const dividerStyle: React.CSSProperties = {
  width: 1,
  height: 24,
  background: 'var(--color-border)',
  margin: '0 4px',
  flexShrink: 0,
};

const userMenuStyle: React.CSSProperties = {
  position: 'absolute', bottom: 70, left: 12, right: 12,
  background: 'var(--color-surface)', border: '1px solid var(--color-border)',
  borderRadius: 10, padding: 6, boxShadow: 'var(--shadow)',
  color: 'var(--color-text)',
};

const menuItemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '8px 10px', borderRadius: 6,
  color: 'var(--color-text)', textDecoration: 'none', fontSize: 13,
  cursor: 'pointer',
};

// ---------- subcomponents ----------

function SidebarLink({ to, label, icon, active }: NavItem & { active: boolean }) {
  return (
    <Link
      to={to}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 12px',
        margin: '1px 0',
        borderRadius: 8,
        color: active ? 'var(--color-accent)' : 'rgba(232,236,242,0.78)',
        background: active ? 'rgba(201, 163, 74, 0.12)' : 'transparent',
        textDecoration: 'none',
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        transition: 'background 120ms ease, color 120ms ease',
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <Icon name={icon} size={16} />
      <span>{label}</span>
    </Link>
  );
}

function IconButton({ children, onClick, title }: { children: React.ReactNode; onClick?: () => void; title?: string }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        width: 36, height: 36, padding: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--color-surface-2)', borderColor: 'var(--color-border)',
        color: 'var(--color-text-muted)',
      }}
    >
      {children}
    </button>
  );
}

function Avatar({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('') || 'U';
  return (
    <div style={{
      width: 32, height: 32, borderRadius: '50%',
      background: 'linear-gradient(135deg, #C9A34A, #A8842F)',
      color: '#1A1305', fontWeight: 700, fontSize: 12,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>{initials}</div>
  );
}

// ---------- breadcrumb derivation ----------

type Crumb = { to?: string; label: string };

// Static map of first-segment → root crumb.
const TOP_LEVEL: Record<string, string> = {
  '':              'Dashboard',
  'onboarding':    'Setup',
  'matters':       'Matters',
  'tasks':         'Tasks',
  'diary':         'Diary',
  'parties':       'Parties',
  'attorneys':     'Attorneys',
  'invoices':      'Invoices',
  'fee-book':      'Fee book',
  'trust':         'Trust ledger',
  'documents':     'Documents',
  'precedents':    'Precedents',
  'signing':       'Signing',
  'settings':      'Settings',
  'platform-admin':'Platform admin',
};

// Hand-tuned labels for known second segments under a few sections.
const SUB_OVERRIDES: Record<string, Record<string, string>> = {
  matters: { new: 'New matter' },
  settings: {
    chambers: 'Chambers',
    profile: 'My profile',
    members: 'Members',
    permissions: 'Permissions',
    rates: 'Rates',
    courts: 'Courts',
    'court-calendar': 'Court calendar',
    integrations: 'Integrations',
    billing: 'Billing',
    compliance: 'Compliance',
  },
  'platform-admin': { plans: 'Plans' },
};

function buildCrumbs(
  pathname: string,
  matterCtx: { id: number; reference: string; name: string } | null,
): Crumb[] {
  const segs = pathname.split('/').filter(Boolean);
  if (segs.length === 0) return [{ label: TOP_LEVEL[''] }];

  const root = segs[0];
  const rootLabel = TOP_LEVEL[root] ?? humanise(root);
  const out: Crumb[] = [{ to: `/${root}`, label: rootLabel }];

  if (segs.length === 1) {
    out[0] = { label: rootLabel }; // single crumb — no link
    return out;
  }

  // Matter detail / sub-routes get the matter reference, not the raw ROWID.
  if (root === 'matters' && segs[1] !== 'new') {
    if (matterCtx) {
      out.push({
        to: segs.length > 2 ? `/matters/${segs[1]}` : undefined,
        label: `${matterCtx.reference} · ${matterCtx.name}`,
      });
    } else {
      out.push({ label: 'Matter' });
    }
    if (segs[2]) out.push({ label: humanise(segs[2]) });
    return out;
  }

  const subMap = SUB_OVERRIDES[root] || {};
  const second = subMap[segs[1]] || humanise(segs[1]);
  out.push({ label: second });
  if (segs[2]) out.push({ label: SUB_OVERRIDES[root]?.[segs[2]] || humanise(segs[2]) });
  return out;
}
